
import React, { useState } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Eye, Trash2, UserPlus, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAdminActions } from '@/hooks/useAdminActions';
import InlineBehavioralChart from '@/components/recruitment/InlineBehavioralChart';
import StatusDropdown from '@/components/admin/StatusDropdown';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import LinkToPositionModal from '@/components/LinkToPositionModal';
import { useSuspiciousThreshold } from '@/hooks/useSuspiciousThreshold';

interface AdminTableRowProps {
  submission: any;
  onViewDetails: (submission: any) => void;
  onSubmissionDeleted: (id: string) => void;
  onStatusUpdate?: (submissionId: string, newStatus: string) => Promise<void>;
  hideProfile?: boolean;
  selectedIdealProfile?: any;
  cognitivePercentile?: number | null;
  isExpanded?: boolean;
  showCompany?: boolean;
  companyName?: string;
  showChart?: boolean;
}

const AdminTableRow: React.FC<AdminTableRowProps> = ({
  submission,
  onViewDetails,
  onSubmissionDeleted,
  onStatusUpdate,
  hideProfile = false,
  selectedIdealProfile,
  cognitivePercentile,
  isExpanded = true,
  showCompany = false,
  companyName,
  showChart = false
}) => {
  const { thresholdSeconds } = useSuspiciousThreshold();
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(submission.status || 'Screening');
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  
  // Check if this is a partial assessment
  const isPartial = submission.assessment_metadata?.is_partial || submission.status === 'partial';
  const [sendingEmail, setSendingEmail] = useState(false);
  const { handleSubmissionDelete } = useAdminActions();
  const { toast } = useToast();

  const handleLinkToPosition = () => {
    setIsLinkModalOpen(true);
  };

  const handleRemove = async () => {
    setIsDeleting(true);
    
    try {
      const success = await handleSubmissionDelete(
        submission.id, 
        () => {
          onSubmissionDeleted(submission.id);
        },
        submission.isCognitiveOnly,
        submission.email,
        submission.full_name
      );
      
      if (!success) {
        console.error('Failed to remove submission');
      }
    } catch (error) {
      console.error('Error in remove handler:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate self-assessment word count
  const getSelfWordCount = () => {
    const selectedWords = submission.selected_words;
    if (!selectedWords) return 0;
    
    // Check for dual assessment structure {self: [...], work: [...]}
    if (selectedWords.self && Array.isArray(selectedWords.self)) {
      return selectedWords.self.length;
    }
    
    // Flat array format
    if (Array.isArray(selectedWords)) {
      return selectedWords.length;
    }
    
    return 0;
  };

  const selfWordCount = getSelfWordCount();

  const getMatchColor = (percentage: number) => {
    if (percentage >= 80) return 'bg-green-100 text-green-800';
    if (percentage >= 60) return 'bg-blue-100 text-blue-800';
    if (percentage >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const handleStatusChange = async (newStatus: string) => {
    if (onStatusUpdate) {
      try {
        await onStatusUpdate(submission.id, newStatus);
        setCurrentStatus(newStatus);
      } catch (error) {
        console.error('Failed to update status:', error);
      }
    } else {
      setCurrentStatus(newStatus);
    }
  };

  const handleSendJobSurveyInvite = async () => {
    if (!submission.email) {
      toast({
        title: "Email Required",
        description: "This submission doesn't have an email address.",
        variant: "destructive"
      });
      return;
    }

    setSendingEmail(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-job-survey-invite', {
        body: {
          candidateName: submission.full_name || 'Candidate',
          candidateEmail: submission.email,
          companyName: 'Our Organization',
          jobTitle: 'this position'
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Invitation Sent!",
        description: `Job behavior survey invitation sent to ${submission.email}`,
      });
    } catch (error) {
      console.error('Error sending job survey invitation:', error);
      toast({
        title: "Failed to Send Invitation",
        description: "There was an error sending the job behavior survey invitation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <>
      <TableRow className="group border-b border-border hover:bg-muted/30 hover:shadow-sm transition-all duration-200">
        <TableCell className="px-4 py-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-foreground">{submission.full_name}</div>
              {isPartial && (
                <Badge className="text-xs bg-orange-100 text-orange-800 transition-all duration-200 hover:scale-105">
                  Partial
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground truncate">{submission.email}</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground/70">{formatDate(submission.completed_at)}</span>
              {selfWordCount > 0 && (
                <span 
                  className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    selfWordCount < 15 
                      ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400' 
                      : 'text-muted-foreground'
                  }`}
                  title="Self-assessment words selected"
                >
                  {selfWordCount}w
                </span>
              )}
              {cognitivePercentile !== null && cognitivePercentile !== undefined && (
                <span className="inline-flex items-center gap-0.5">
                  <span 
                    className="text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400"
                    title="Cognitive percentile"
                  >
                    {cognitivePercentile}%
                  </span>
                  {cognitivePercentile >= 90 && submission.cognitiveDuration != null && submission.cognitiveDuration < thresholdSeconds && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-medium">⚠️ Suspicious</p>
                          <p className="text-xs text-muted-foreground">Completed in {Math.floor(submission.cognitiveDuration / 60)}m {submission.cognitiveDuration % 60}s — faster than normal</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </span>
              )}
            </div>
          </div>
        </TableCell>

      {showChart && (
        <TableCell className="px-4 py-3 text-center">
          {submission.scores !== null && submission.scores !== undefined ? (
            <InlineBehavioralChart 
              scores={submission.scores} 
              assessmentVariant={submission.assessment_variant}
            />
          ) : (
            <span className="text-muted-foreground text-sm">-</span>
          )}
        </TableCell>
      )}


      <TableCell className="px-4 py-3 text-center">
        <div className="space-y-1">
          <div className="flex items-center justify-center gap-1.5">
            <div className="text-xs font-medium text-foreground">
              {submission.profileType || 'Unknown'}
            </div>
            {submission.assessment_variant === 'model2' && (
              <Badge 
                variant="secondary"
                className="text-[10px] px-1.5 py-0 h-4 bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800"
              >
                M2
              </Badge>
            )}
            {submission.assessment_variant === 'model3' && (
              <Badge 
                variant="secondary"
                className="text-[10px] px-1.5 py-0 h-4 bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-300 dark:border-teal-800"
              >
                M3
              </Badge>
            )}
          </div>
          {[submission.aPercentile, submission.bPercentile, submission.cPercentile, submission.dPercentile]
            .every(p => typeof p === 'number' && p >= 45 && p <= 55) && (
            <Badge 
              variant="secondary"
              className="text-[10px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800"
            >
              Narrow
            </Badge>
          )}
          {!hideProfile && submission.profile_name && (
            <div className="text-xs text-muted-foreground">
              {submission.profile_name}
            </div>
          )}
        </div>
      </TableCell>

      <TableCell className="px-4 py-3 text-center">
        <div className="flex justify-center">
          <StatusDropdown
            submissionId={submission.id}
            currentStatus={currentStatus}
            onStatusChange={handleStatusChange}
            isPartial={isPartial}
          />
        </div>
      </TableCell>

      {selectedIdealProfile && (
        <TableCell className="px-4 py-3 text-center">
          <Badge className={`text-xs ${getMatchColor(submission.matchPercentage)}`}>
            {submission.matchPercentage}%
          </Badge>
        </TableCell>
      )}

      <TableCell className="px-4 py-3">
        <div className="flex items-center gap-1 justify-start">
          {/* Always render the button container to maintain alignment, but hide for cognitive-only */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLinkToPosition}
            className={`h-8 w-8 p-0 hover:bg-muted/30 transition-colors duration-fast ${submission.isCognitiveOnly ? 'invisible' : ''}`}
            title="Link to position"
            disabled={submission.isCognitiveOnly}
          >
            <UserPlus className="h-4 w-4 text-muted-foreground" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetails(submission)}
            className="h-8 w-8 p-0 hover:bg-muted/30 transition-colors duration-fast"
            title="View details"
          >
            <Eye className="h-4 w-4 text-primary" />
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-muted/30 transition-colors duration-fast"
                disabled={isDeleting}
                title="Remove from company"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove Candidate</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to remove {submission.full_name} from your company's view? 
                  Their assessment data will be preserved but they will no longer appear in your dashboard.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRemove}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Removing...' : 'Remove'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
      
    <LinkToPositionModal
      isOpen={isLinkModalOpen}
      onClose={() => setIsLinkModalOpen(false)}
      candidateId={submission.id}
      candidateName={submission.full_name}
    />
    </>
  );
};

export default AdminTableRow;
